import React from "react";
import { Play, Video, FileText, Clock, Users2, Share2 } from "lucide-react";
import FeaturePage, { MockWindow } from "../features/FeaturePage";
import { siblingSolutions, DEMO_CTA } from "./links";

const ACCENT = "#7C3AED";

// ── Hero visual: recorded playback ──
function PlayerVisual() {
  return (
    <MockWindow label="INTERVIEW RECORDING · A. OKAFOR" badge="18:24" accent={ACCENT}>
      <div className="space-y-3">
        <div className="rounded-xl bg-[#0F172A] h-32 flex items-center justify-center relative overflow-hidden">
          <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: `${ACCENT}` }}>
            <Play className="w-6 h-6 text-white ml-0.5" />
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-white/20">
            <div className="h-1.5" style={{ width: "42%", background: ACCENT }} />
          </div>
          <span className="absolute bottom-2.5 right-3 font-mono text-[9px] text-white/80">07:41 / 18:24</span>
        </div>
        <div className="flex gap-2">
          {["Q1", "Q2", "Q3", "Q4"].map((q, i) => (
            <div key={q} className="flex-1 rounded-lg border border-border p-1.5 text-center font-mono text-[9px]"
              style={{ borderColor: i === 1 ? `${ACCENT}66` : undefined, color: i === 1 ? ACCENT : "#94A3B8" }}>
              {q}
            </div>
          ))}
        </div>
        <p className="font-mono text-[10px] text-muted-foreground text-center">Jump to any question in the recording</p>
      </div>
    </MockWindow>
  );
}

// ── Row visual: transcript alongside video ──
function TranscriptVisual() {
  return (
    <MockWindow label="RECORDING + TRANSCRIPT" accent={ACCENT}>
      <div className="space-y-2.5">
        {[
          { role: "Interviewer", text: "How do you de-escalate an upset customer?", me: false },
          { role: "Candidate", text: "I acknowledge the frustration first, take ownership, then focus on the one fix that unblocks them…", me: true },
          { role: "Interviewer", text: "Can you give a specific example?", me: false },
        ].map((m, i) => (
          <div key={i} className={`rounded-xl border p-3 ${m.me ? "" : "bg-secondary/30"}`}
            style={m.me ? { borderColor: `${ACCENT}33`, background: `${ACCENT}0D` } : { borderColor: "#E5E7EB" }}>
            <div className="font-mono text-[9px] tracking-[0.15em] mb-1" style={{ color: m.me ? ACCENT : "#94A3B8" }}>{m.role.toUpperCase()}</div>
            <p className="text-xs text-[#0F172A] leading-snug">{m.text}</p>
          </div>
        ))}
      </div>
    </MockWindow>
  );
}

// ── Row visual: share with hiring team ──
function ShareVisual() {
  return (
    <MockWindow label="SHARE WITH YOUR TEAM" accent={ACCENT}>
      <div className="space-y-3">
        {["Hiring manager", "Team lead", "Recruiter"].map((r, i) => (
          <div key={r} className="flex items-center gap-3 rounded-xl border border-border p-2.5">
            <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-bold" style={{ background: ACCENT }}>
              {r[0]}
            </div>
            <span className="text-xs text-[#0F172A] flex-1">{r}</span>
            <span className="font-mono text-[9px] text-muted-foreground">can review</span>
          </div>
        ))}
        <div className="flex items-center gap-2 rounded-xl border p-3" style={{ borderColor: `${ACCENT}33`, background: `${ACCENT}0D` }}>
          <Share2 className="w-3.5 h-3.5 flex-shrink-0" style={{ color: ACCENT }} />
          <span className="text-[11px] text-muted-foreground leading-snug">Secure, time-limited playback links — no downloads to pass around.</span>
        </div>
      </div>
    </MockWindow>
  );
}

export default function VideoInterviewsSolution() {
  return (
    <FeaturePage
      accent={ACCENT}
      signupRole="company"
      secondaryCta={DEMO_CTA}
      eyebrow="VIDEO INTERVIEWS"
      title="Every interview,"
      titleAccent="on the record."
      subtitle="Each AI interview is recorded end-to-end and paired with a full transcript, so your team can watch the moments that matter, revisit an answer, and make the call together — long after the candidate has logged off."
      heroChips={["Full-session recording", "Synced transcript", "Secure team playback"]}
      heroVisual={<PlayerVisual />}
      rows={[
        {
          tag: "FULL RECORDING",
          title: "Rewatch the whole interview, any time.",
          body: "The entire session is captured, so you're never relying on notes or a score alone. Jump to a specific question, replay a shaky answer, or sanity-check a strong one before you advance a candidate.",
          bullets: [
            "Complete end-to-end session recording",
            "Jump straight to any question",
            "Revisit weeks later, on demand",
          ],
          visual: <PlayerVisual />,
        },
        {
          tag: "RECORDING + TRANSCRIPT",
          title: "Read it or watch it — your call.",
          body: "Every recording comes with a full transcript of the questions, answers, and follow-ups. Skim the text to find the moment, then hit play to see exactly how it was delivered.",
          bullets: [
            "Full question-and-answer transcript",
            "Follow-up exchanges captured too",
            "Find the moment fast, then watch it",
          ],
          visual: <TranscriptVisual />,
          reverse: true,
        },
        {
          tag: "SHARE SECURELY",
          title: "Bring the whole panel in — without a meeting.",
          body: "Let hiring managers and teammates review the same recording on their own time through secure, time-limited links. Decisions get made async, with everyone seeing the real interview instead of second-hand notes.",
          bullets: [
            "Secure, time-limited playback links",
            "Async review across the hiring team",
            "No files to download or email around",
          ],
          visual: <ShareVisual />,
        },
      ]}
      steps={[
        { title: "Candidate interviews", body: "The AI interview is recorded automatically, start to finish." },
        { title: "Recording + transcript ready", body: "Playback and a synced transcript appear on the candidate's result." },
        { title: "Review & share", body: "Watch, jump to questions, and share secure links with your team." },
      ]}
      useCases={[
        { icon: Users2, persona: "Panel decisions", body: "Give every stakeholder the real interview to review, without lining up calendars." },
        { icon: Clock, persona: "Busy hiring managers", body: "Skim the transcript, watch only the parts that matter, decide in minutes." },
        { icon: FileText, persona: "Audit & fairness", body: "Keep a complete record of what was asked and answered for every candidate." },
      ]}
      faqs={[
        { q: "Is the whole interview recorded?", a: "Yes — the full session is captured from start to finish and attached to the candidate's result, alongside a synced transcript." },
        { q: "Can my team watch without an account each?", a: "You share secure, time-limited playback links, so the right people can review a recording without passing files around or downloading anything." },
        { q: "Is there a transcript too?", a: "Every recording includes a full transcript of questions, answers, and follow-ups, so you can read first and watch the key moments." },
        { q: "Do candidates know they're recorded?", a: "Yes. Recording is transparent to the candidate and pairs with your consent settings for a compliant, above-board process." },
        { q: "How long are recordings kept?", a: "Recordings follow your data-retention settings and can be removed when a candidate is deleted — see the Compliance solution for details." },
      ]}
      ctaTitle="See the whole interview."
      ctaSubtitle="Start free, or book a demo to watch a sample session."
      related={siblingSolutions("video-interviews")}
      seoTitle="Recorded Video Interviews with Transcripts | VetraHub"
      seoDescription="Every AI interview is recorded end-to-end with a synced transcript. Jump to any question, review async, and share secure playback links with your hiring team."
    />
  );
}
